import type { Dispatch, SetStateAction } from "react";
import InfoTooltip from "../../../shared/components/InfoTooltip";
import ImageUploadField from "../../../shared/components/ImageUploadField";
import OrganizerDatePicker, {
  formatDateInputValue,
} from "../../../shared/components/OrganizerDatePicker";
import OrganizerTimeInput from "../../../shared/components/OrganizerTimeInput";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import type { ChallengeEventDraft } from "../../challenges/types/challengeDraft";
import {
  DEFAULT_TOURNAMENT_SHIRT_SIZES,
  normalizeTournamentShirtSizes,
} from "../utils/tournamentCategories";
import {
  MIN_EVENT_AMOUNT_CENTS,
  parseAmountToCents,
} from "../utils/adminChallengeEditor";

type AdminChallengeEventSettingsFieldsProps = {
  challengeEventDraft: ChallengeEventDraft;
  registrationTypeLabel: string;
  setChallengeEventDraft: Dispatch<SetStateAction<ChallengeEventDraft>>;
};

function buildShirtSizeOptions(selectedSizes: string[]) {
  const selected = normalizeTournamentShirtSizes(selectedSizes);
  const options = [...DEFAULT_TOURNAMENT_SHIRT_SIZES];
  selected.forEach((size) => {
    if (
      !options.some(
        (option) => option.trim().toLowerCase() === size.trim().toLowerCase(),
      )
    ) {
      options.push(size);
    }
  });
  return options;
}

function parseDateTime(value?: string | null) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

function getDateValue(value?: string | null) {
  const parsed = parseDateTime(value);
  return parsed ? formatDateInputValue(parsed) : "";
}

function getTimeValue(value?: string | null) {
  const parsed = parseDateTime(value);
  if (!parsed) return "";
  return `${String(parsed.getHours()).padStart(2, "0")}:${String(
    parsed.getMinutes(),
  ).padStart(2, "0")}`;
}

function buildDateTimeIso(dateValue: string, timeValue: string) {
  const dateMatch = String(dateValue || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!dateMatch) return "";
  const timeMatch = String(timeValue || "23:59").match(/^(\d{1,2}):(\d{2})$/);
  const hours = timeMatch ? Number(timeMatch[1]) : 23;
  const minutes = timeMatch ? Number(timeMatch[2]) : 59;
  if (
    !Number.isFinite(hours) ||
    !Number.isFinite(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return "";
  }
  const [, year, month, day] = dateMatch;
  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    hours,
    minutes,
    0,
    0,
  ).toISOString();
}

export function AdminChallengeEventAmountField({
  challengeEventDraft,
  setChallengeEventDraft,
}: {
  challengeEventDraft: ChallengeEventDraft;
  setChallengeEventDraft: Dispatch<SetStateAction<ChallengeEventDraft>>;
}) {
  const createTexts = siteTexts.admin.createChallengePage;
  const info = siteTexts.common.info;
  const amountPerPlayerCents = parseAmountToCents(
    challengeEventDraft.amountPerPlayer || "",
  );
  const showAmountMinimumError =
    String(challengeEventDraft.amountPerPlayer || "").trim() !== "" &&
    (!Number.isFinite(Number(amountPerPlayerCents)) ||
      Number(amountPerPlayerCents) < MIN_EVENT_AMOUNT_CENTS);

  return (
    <label className="field admin-form-block-price">
      <span className="info-label">
        {createTexts.pricePerPlayer}
        <InfoTooltip content={info.valuePerPlayer} />
      </span>
      <input
        type="text"
        inputMode="decimal"
        required
        value={challengeEventDraft.amountPerPlayer}
        onChange={(event) =>
          setChallengeEventDraft((prev) => ({
            ...prev,
            amountPerPlayer: event.target.value,
          }))
        }
      />
      {showAmountMinimumError && (
        <small className="field-error">{createTexts.toasts.priceMinimum}</small>
      )}
    </label>
  );
}

export function AdminChallengeEventImageField({
  challengeEventDraft,
  setChallengeEventDraft,
}: {
  challengeEventDraft: ChallengeEventDraft;
  setChallengeEventDraft: Dispatch<SetStateAction<ChallengeEventDraft>>;
}) {
  const t = siteTexts.admin;

  return (
    <ImageUploadField
      label={t.labels.image}
      value={challengeEventDraft.imageUrl}
      previewSquare
      onChange={(nextValue) =>
        setChallengeEventDraft((prev) => ({
          ...prev,
          imageUrl: nextValue,
        }))
      }
      onRemove={() =>
        setChallengeEventDraft((prev) => ({
          ...prev,
          imageUrl: "",
        }))
      }
    />
  );
}

export function AdminChallengeShirtSettingsFields({
  challengeEventDraft,
  selectionValue,
  selectionTouched,
  onSelectionChange,
  onSelectionTouched,
  setChallengeEventDraft,
}: {
  challengeEventDraft: ChallengeEventDraft;
  selectionValue?: "" | "true" | "false";
  selectionTouched?: boolean;
  onSelectionChange?: (value: "true" | "false") => void;
  onSelectionTouched?: () => void;
  setChallengeEventDraft: Dispatch<SetStateAction<ChallengeEventDraft>>;
}) {
  const t = siteTexts.admin;
  const createTexts = t.createChallengePage;
  const selectedShirtSizes = normalizeTournamentShirtSizes(
    challengeEventDraft.shirtSizes,
  );
  const selectedShirtSizeKeys = new Set(
    selectedShirtSizes.map((size) => size.trim().toLowerCase()),
  );
  const shirtSizeOptions = buildShirtSizeOptions(selectedShirtSizes);
  const shirtTexts = createTexts.shirts;
  const info = siteTexts.common.info;
  const deadlineDate = getDateValue(challengeEventDraft.shirtDeadlineAt);
  const deadlineTime = getTimeValue(challengeEventDraft.shirtDeadlineAt);

  return (
    <div className="admin-event-inline-editor admin-form-block-shirts">
      <label className="field">
        <span className="info-label">
          {t.tournamentCategoryEditor.shirtSizesEnabled}
          <InfoTooltip content={info.eventShirts} />
        </span>
        <select
          value={
            selectionValue !== undefined
              ? selectionValue
              : selectionTouched === false
                ? ""
                : challengeEventDraft.shirtSizesEnabled
                  ? "true"
                  : "false"
          }
          onChange={(event) => {
            const selectedValue =
              event.target.value === "true" ? "true" : "false";
            onSelectionChange?.(selectedValue);
            onSelectionTouched?.();
            const enabled = selectedValue === "true";
            setChallengeEventDraft((prev) => ({
              ...prev,
              shirtSizesEnabled: enabled,
              shirtSizes: enabled
                ? prev.shirtSizes.length > 0
                  ? prev.shirtSizes
                  : DEFAULT_TOURNAMENT_SHIRT_SIZES
                : [],
              shirtAvailabilityType: enabled
                ? prev.shirtAvailabilityType || "none"
                : "none",
              shirtDeadlineAt: enabled ? prev.shirtDeadlineAt : "",
              shirtQuantityLimit: enabled ? prev.shirtQuantityLimit : "",
            }));
          }}
        >
          {selectionTouched === false && (
            <option value="" disabled>
              Selecione
            </option>
          )}
          <option value="false">
            {t.tournamentCategoryEditor.shirtSizesDisabled}
          </option>
          <option value="true">
            {t.tournamentCategoryEditor.shirtSizesEnabledOption}
          </option>
        </select>
      </label>
      <fieldset className="field admin-shirt-size-field">
        <legend>
          <span className="info-label">
            {t.tournamentCategoryEditor.shirtSizes}
            <InfoTooltip content={info.shirt} />
          </span>
        </legend>
        <div className="admin-shirt-size-options">
          {shirtSizeOptions.map((size) => {
            const key = size.trim().toLowerCase();
            const checked = selectedShirtSizeKeys.has(key);
            return (
              <label key={key} className="admin-shirt-size-option">
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={!challengeEventDraft.shirtSizesEnabled}
                  onChange={(event) => {
                    setChallengeEventDraft((prev) => {
                      const current = normalizeTournamentShirtSizes(
                        prev.shirtSizes,
                      );
                      const next = event.target.checked
                        ? [...current, size]
                        : current.filter(
                            (item) =>
                              item.trim().toLowerCase() !==
                              size.trim().toLowerCase(),
                          );
                      return {
                        ...prev,
                        shirtSizes: normalizeTournamentShirtSizes(next),
                      };
                    });
                  }}
                />
                <span>{size}</span>
              </label>
            );
          })}
        </div>
      </fieldset>
      {challengeEventDraft.shirtSizesEnabled && (
        <>
          <label className="field">
            <span className="info-label">
              {shirtTexts.availabilityType}
              <InfoTooltip content={info.eventShirts} />
            </span>
            <select
              value={challengeEventDraft.shirtAvailabilityType}
              onChange={(event) =>
                setChallengeEventDraft((prev) => ({
                  ...prev,
                  shirtAvailabilityType: event.target.value as
                    | "none"
                    | "period"
                    | "quantity",
                }))
              }
            >
              <option value="none">{shirtTexts.availabilityNone}</option>
              <option value="period">{shirtTexts.availabilityPeriod}</option>
              <option value="quantity">{shirtTexts.availabilityQuantity}</option>
            </select>
          </label>
          {challengeEventDraft.shirtAvailabilityType === "period" && (
            <div className="admin-row-two">
              <OrganizerDatePicker
                label={shirtTexts.deadline}
                value={deadlineDate}
                disabledBefore={formatDateInputValue(new Date())}
                onChange={(value) =>
                  setChallengeEventDraft((prev) => ({
                    ...prev,
                    shirtDeadlineAt: buildDateTimeIso(value, deadlineTime),
                  }))
                }
              />
              <OrganizerTimeInput
                label={
                  <span className="info-label">
                  {siteTexts.admin.labels.time}
                  <InfoTooltip content={info.shirtsByPeriod} />
                  </span>
                }
                value={deadlineTime}
                onChange={(value) =>
                  setChallengeEventDraft((prev) => ({
                    ...prev,
                    shirtDeadlineAt: buildDateTimeIso(deadlineDate, value),
                  }))
                }
                required
                disabled={!deadlineDate}
              />
            </div>
          )}
          {challengeEventDraft.shirtAvailabilityType === "quantity" && (
            <label className="field">
              <span className="info-label">
                {shirtTexts.quantityLimit}
                <InfoTooltip content={info.shirtsByQuantity} />
              </span>
              <input
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                required
                value={challengeEventDraft.shirtQuantityLimit}
                onChange={(event) =>
                  setChallengeEventDraft((prev) => ({
                    ...prev,
                    shirtQuantityLimit: event.target.value,
                  }))
                }
              />
            </label>
          )}
          {challengeEventDraft.shirtAvailabilityType === "quantity" && (
            <p className="status-message">
              {shirtTexts.quantitySummary
                .replace(
                  "{total}",
                  String(challengeEventDraft.shirtQuantityLimit || 0),
                )
                .replace(
                  "{confirmed}",
                  String(challengeEventDraft.shirtConfirmedCount || 0),
                )
                .replace(
                  "{available}",
                  String(challengeEventDraft.shirtAvailableCount ?? "-"),
                )}
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default function AdminChallengeEventSettingsFields({
  challengeEventDraft,
  registrationTypeLabel,
  setChallengeEventDraft,
}: AdminChallengeEventSettingsFieldsProps) {
  const createTexts = siteTexts.admin.createChallengePage;

  return (
    <div className="admin-event-inline-editor admin-form-block-media">
      <AdminChallengeEventImageField
        challengeEventDraft={challengeEventDraft}
        setChallengeEventDraft={setChallengeEventDraft}
      />
      <p className="status-message">
        {createTexts.registrationTypeInfoPrefix} {registrationTypeLabel}
      </p>
      <AdminChallengeShirtSettingsFields
        challengeEventDraft={challengeEventDraft}
        setChallengeEventDraft={setChallengeEventDraft}
      />
    </div>
  );
}
